import React from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import { Header } from './Header';
import { TargetApp } from '../../target-app/TargetApp';
import { IncidentTimeline } from '../Dashboard/IncidentTimeline';
import { StateInspector } from '../Dashboard/StateInspector';
import { PatchViewer } from '../Dashboard/PatchViewer';
import { ToolRegistry } from '../WebMCP/ToolRegistry';
import { TelemetryFeed } from '../WebMCP/TelemetryFeed';
import { DeployPanel } from '../ControlCenter/DeployPanel';
import { CustomPlayground } from '../ControlCenter/CustomPlayground';
import { SimulationBar } from '../ControlCenter/SimulationBar';
import { ASTSafetyAuditor } from '../Security/ASTSafetyAuditor';
import { EmbedSDKPanel } from '../Embed/EmbedSDKPanel';
import {
  Activity,
  Database,
  GitPullRequest,
  Sparkles,
  Terminal,
  CloudUpload,
  Play,
  ShieldCheck,
  Globe,
} from 'lucide-react';

export const AppShell: React.FC = () => {
  const { activeTab, setActiveTab, activeIncident, systemStatus } = useAutoFix();

  return (
    <div className="app-shell">
      {/* Top Fixed Header */}
      <Header />

      {/* Main Split-Pane Workspace */}
      <main className="main-content">
        {/* Left Pane: Target E-Commerce App */}
        <section className="left-pane">
          <TargetApp />
        </section>

        {/* Right Pane: AutoFix Incident Control Center */}
        <section className="right-pane" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Top Simulation Bar (Slim 44px Quick Trigger HUD) */}
          <SimulationBar />

          {/* Right Header Navigation Tabs */}
          <div className="pane-header">
            <div className="tab-bar" style={{ width: 'auto', flexWrap: 'wrap' }}>
              <button
                className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Activity size={12} style={{ color: activeTab === 'timeline' ? 'var(--accent)' : 'inherit' }} />
                <span>Incident Timeline</span>
                {systemStatus === 'incident' && (
                  <span className="status-dot status-incident" style={{ width: 6, height: 6 }} />
                )}
                {systemStatus === 'healing' && (
                  <span className="status-dot status-healing" style={{ width: 6, height: 6 }} />
                )}
              </button>

              <button
                className={`tab ${activeTab === 'state' ? 'active' : ''}`}
                onClick={() => setActiveTab('state')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Database size={12} style={{ color: activeTab === 'state' ? 'var(--accent)' : 'inherit' }} />
                <span>Live State Tree</span>
              </button>

              <button
                className={`tab ${activeTab === 'patch' ? 'active' : ''}`}
                onClick={() => setActiveTab('patch')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <GitPullRequest size={12} style={{ color: activeTab === 'patch' ? 'var(--accent)' : 'inherit' }} />
                <span>Patch Diff</span>
                {activeIncident?.patch && (
                  <span className="badge badge-green" style={{ fontSize: 8, padding: '1px 4px' }}>
                    READY
                  </span>
                )}
              </button>

              <button
                className={`tab ${activeTab === 'security' ? 'active' : ''}`}
                onClick={() => setActiveTab('security')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <ShieldCheck size={12} style={{ color: activeTab === 'security' ? 'var(--green)' : 'inherit' }} />
                <span>AST Safety</span>
              </button>

              <button
                className={`tab ${activeTab === 'embed' ? 'active' : ''}`}
                onClick={() => setActiveTab('embed')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Globe size={12} style={{ color: activeTab === 'embed' ? '#38bdf8' : 'inherit' }} />
                <span>SDK Embed</span>
              </button>

              <button
                className={`tab ${activeTab === 'deploy' ? 'active' : ''}`}
                onClick={() => setActiveTab('deploy')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <CloudUpload size={12} style={{ color: activeTab === 'deploy' ? '#38bdf8' : 'inherit' }} />
                <span>Netlify Deploy</span>
              </button>

              <button
                className={`tab ${activeTab === 'tools' ? 'active' : ''}`}
                onClick={() => setActiveTab('tools')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Sparkles size={12} style={{ color: activeTab === 'tools' ? 'var(--accent)' : 'inherit' }} />
                <span>WebMCP Tools</span>
              </button>

              <button
                className={`tab ${activeTab === 'telemetry' ? 'active' : ''}`}
                onClick={() => setActiveTab('telemetry')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Terminal size={12} style={{ color: activeTab === 'telemetry' ? 'var(--accent)' : 'inherit' }} />
                <span>Telemetry</span>
              </button>

              <button
                className={`tab ${activeTab === 'playground' ? 'active' : ''}`}
                onClick={() => setActiveTab('playground')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Play size={12} style={{ color: activeTab === 'playground' ? 'var(--purple)' : 'inherit' }} />
                <span>Playground</span>
              </button>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
              W3C WebMCP Protocol
            </div>
          </div>

          {/* Right Pane Scrollable Body - FULL HEIGHT */}
          <div className="pane-body" style={{ flex: 1, overflowY: 'auto' }}>
            {activeTab === 'timeline' && <IncidentTimeline />}
            {activeTab === 'state' && <StateInspector />}
            {activeTab === 'patch' && <PatchViewer />}
            {activeTab === 'security' && <ASTSafetyAuditor />}
            {activeTab === 'embed' && <EmbedSDKPanel />}
            {activeTab === 'deploy' && <DeployPanel />}
            {activeTab === 'tools' && <ToolRegistry />}
            {activeTab === 'telemetry' && <TelemetryFeed />}
            {activeTab === 'playground' && <CustomPlayground />}
          </div>
        </section>
      </main>
    </div>
  );
};
